/**
 * TODO 6.1 — Step0Business.jsx
 * Tela 0 — Seleção de Negócio (tema)
 * 
 * Props: wizard (useWelcomeWizard)
 * - Sem barra de progresso
 * - Grid de 13 temas
 * - Tema "default" (Outros) por último com campo de customBusinessType
 * - PUT /users/theme ao selecionar
 * - Botão "Começar" habilita após seleção + (se Outros) campo preenchido
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Grid2 as Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  MiscellaneousServices,
  ContentCut,
  Spa,
  MedicalServices,
  FavoriteBorder,
  Palette,
  Psychology,
  SelfImprovement,
  Healing,
  LocalHospital,
  Favorite,
  School,
  FitnessCenter,
} from '@mui/icons-material';
import { loadTemplate } from '../../../utils/templateLoader';
import themesService from '../../../services/themesService';
import usersService from '../../../services/usersService';

export default function Step0Business({ wizard }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedThemeId, setSelectedThemeId] = useState(null);
  const [customBusinessType, setCustomBusinessType] = useState('');
  const [savingTheme, setSavingTheme] = useState(false);
  const [error, setError] = useState(null);

  // Carrega os temas ativos (tabela `theme`, D10) + visual de cada card (cor/ícone, seção
  // `admin` do {id}.json no Static, D12) ao montar. "default" (Outros) sempre por último.
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);
        const activeThemes = await themesService.getActiveThemes();
        const visuals = await Promise.all(activeThemes.map((t) => loadTemplate(t.id)));
        const list = activeThemes.map((t, i) => ({
          id: t.id,
          name: t.displayName,
          description: visuals[i]?.description,
          colors: visuals[i]?.colors,
          icons: visuals[i]?.icons,
        }));

        const others = list
          .filter((t) => t.id !== 'default')
          .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
        const def = list.find((t) => t.id === 'default');
        setTemplates(def ? [...others, def] : others);
      } catch (err) {
        console.error('[Step0Business] Erro ao carregar templates:', err);
        setError('Erro ao carregar temas. Tente recarregar.');
      } finally {
        setLoading(false);
      }
    };
    loadTemplates();
  }, []);

  // Mapa de ícones MUI por tema — Achado 7: conectar ícones reais por tema
  const themeIconMap = {
    'default': null,
    'barbershop': ContentCut,
    'company': MedicalServices,
    'tattoo-studio': Palette,
    'beauty-salon': Spa,
    'psychology': Psychology,
    'aesthetics': Favorite,
    'massage': SelfImprovement,
    'dental': Healing,
    'medical': LocalHospital,
    'therapy': Healing,
    'teacher': School,
    'personal-trainer': FitnessCenter,
  };

  // Retorna o ícone MUI apropriado para o tema, ou null para usar emoji como fallback
  const getThemeIcon = (template) => {
    return themeIconMap[template.id] || null;
  };

  // Seleciona um tema
  const handleSelectTheme = (themeId) => {
    setSelectedThemeId(themeId);
    setError(null);
    // Se não é "Outros", reseta o campo customBusinessType
    if (themeId !== 'default') {
      setCustomBusinessType('');
    }
  };

  // Confirma e avança
  const handleBegin = async () => {
    if (!selectedThemeId) {
      setError('Selecione um tema para começar');
      return;
    }

    // Se é "Outros", valida o campo
    if (selectedThemeId === 'default' && !customBusinessType.trim()) {
      setError('Descreva seu negócio para continuar');
      return;
    }

    try {
      setSavingTheme(true);
      setError(null);

      // PUT /users/theme via usersService — selectedThemeId já é o theme-id string (D12, onda 3)
      await usersService.updateTheme(selectedThemeId);

      // Atualiza o wizard
      wizard.setTheme(selectedThemeId, selectedThemeId === 'default' ? customBusinessType : null);
      
      // Avança para Tela 1
      wizard.goNext();
    } catch (err) {
      console.error('[Step0Business] Erro ao salvar tema:', err);
      setError(err.message || 'Erro ao salvar tema');
    } finally {
      setSavingTheme(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: '40px 20px',
        }}
      >
        <CircularProgress />
        <Typography sx={{ marginTop: 2, color: '#666' }}>
          Carregando temas...
        </Typography>
      </Box>
    );
  }

  // Tema selecionado para acessar cores — Achado 2: tokenizar cores
  const selectedTemplate = selectedThemeId ? templates.find(t => t.id === selectedThemeId) : null;
  const textColor = selectedTemplate?.colors?.text || '#333';
  const textSecondaryColor = selectedTemplate?.colors?.textSecondary || '#666';
  const backgroundColor = selectedTemplate?.colors?.background || '#fff';
  const dividerColor = selectedTemplate?.colors?.divider || 'rgba(0,0,0,0.12)';

  const isOthersSelected = selectedThemeId === 'default';
  const canBegin = selectedThemeId && (!isOthersSelected || customBusinessType.trim().length > 0);

  return (
    <Box
      component="section"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '60px 20px 40px',
        maxWidth: '1100px',
        margin: '0 auto',
      }}
    >
      {/* Cabeçalho */}
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 600,
          textAlign: 'center',
          marginBottom: '12px',
          color: textColor,
        }}
      >
        Qual é o seu ramo de negócio?
      </Typography>
      <Typography
        sx={{
          textAlign: 'center',
          color: textSecondaryColor,
          marginBottom: '40px',
          fontSize: '14px',
        }}
      >
        Escolha o tema que mais se encaixa na sua profissão — os textos e sugestões vão ser personalizados para você!
      </Typography>

      {/* Erro */}
      {error && (
        <Alert severity="error" sx={{ marginBottom: '20px', width: '100%' }}>
          {error}
        </Alert>
      )}

      {/* Grid de temas */}
      <Grid
        container
        spacing={2}
        sx={{
          marginBottom: '40px',
          width: '100%',
        }}
      >
        {templates.map((template) => {
          const isSelected = selectedThemeId === template.id;
          const isOthers = template.id === 'default';

          return (
            <Grid key={template.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Card
                onClick={() => handleSelectTheme(template.id)}
                sx={{
                  cursor: 'pointer',
                  border: isSelected ? '3px solid' : '1px solid',
                  borderColor: isSelected
                    ? template.colors?.primary || '#333'
                    : template.colors?.divider || 'rgba(0,0,0,0.12)',
                  backgroundColor: isSelected 
                    ? template.colors?.background || '#f9f9f9'
                    : template.colors?.background || '#fff',
                  transition: 'all 0.25s ease-in-out',
                  transform: isSelected ? 'scale(1.03)' : 'scale(1)',
                  boxShadow: isSelected
                    ? `0 4px 12px ${template.colors?.primary || '#333'}22`
                    : '0 1px 3px rgba(0,0,0,0.1)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    transform: 'scale(1.02)',
                  },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    flex: 1,
                    padding: '24px 16px',
                  }}
                >
                  {/* Ícone — Achado 7: usar ícone MUI colorizado pelo tema */}
                  <Box
                    sx={{
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: template.colors?.primary || '#333',
                    }}
                  >
                    {getThemeIcon(template) ? (
                      // Ícone MUI colorizado
                      (() => {
                        const IconComponent = getThemeIcon(template);
                        return <IconComponent sx={{ fontSize: '40px', color: 'inherit' }} />;
                      })()
                    ) : (
                      // Fallback para emoji
                      <span style={{ fontSize: '40px' }}>
                        {isOthers ? '🎯' : template.icons?.services?.emoji || '🏢'}
                      </span>
                    )}
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: template.colors?.text || '#333',
                      marginBottom: '4px',
                    }}
                  >
                    {isOthers ? 'Outros' : template.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: template.colors?.textSecondary || '#666',
                    }}
                  >
                    {template.description || ''}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Campo customBusinessType — aparece quando "Outros" é selecionado */}
      {isOthersSelected && (
        <Box sx={{ width: '100%', marginBottom: '40px' }}>
          <Typography
            sx={{
              fontWeight: 600,
              marginBottom: '12px',
              color: '#333',
            }}
          >
            Descreva seu negócio
          </Typography>
          <TextField
            fullWidth
            placeholder="Ex: Estúdio de música, Pet shop, Mecânica..."
            value={customBusinessType}
            onChange={(e) => setCustomBusinessType(e.target.value)}
            variant="outlined"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canBegin) {
                handleBegin();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
              },
            }}
          />
          <Typography
            sx={{
              fontSize: '12px',
              color: textSecondaryColor,
              marginTop: '8px',
            }}
          >
            Isso vai aparecer no seu perfil e ajuda a gente a sugerir configurações mais adequadas.
          </Typography>
        </Box>
      )}

      {/* Botão Começar */}
      <Button
        variant="contained"
        size="large"
        onClick={handleBegin}
        disabled={!canBegin || savingTheme}
        sx={{
          textTransform: 'none',
          fontSize: '16px',
          fontWeight: 600,
          paddingX: '48px',
          paddingY: '12px',
        }}
      >
        {savingTheme ? <CircularProgress size={24} /> : 'Começar'}
      </Button>
    </Box>
  );
}
